/**
 * Fail-closed reader for the verified-design-libraries registry.
 *
 * The registry is the committed, sync-tested projection of the
 * docs/figma-sources admission evidence (see build-manifest.ts). It is
 * imported at build time — same pattern as
 * features/chat/services/report-application-integration.ts — so the client
 * never fetches it, and "unreadable" means the bundled bytes no longer parse
 * as the schema this client was built against (generator/client schema
 * drift, or a corrupted bundle). The answer to that state is an explicit
 * error surface with NO library list: this screen does not guess, and it
 * never falls back to a hardcoded roster.
 */
import manifestJson from '@/data/verified-design-libraries.manifest.v1.json' with {
  type: 'json',
}
import {
  verifiedDesignLibrariesManifestSchema,
  type VerifiedDesignLibrariesManifest,
} from './manifest-schema'

export type VerifiedDesignLibrariesRead =
  | { status: 'ready'; manifest: VerifiedDesignLibrariesManifest }
  | { status: 'unreadable'; reason: string }

export function parseVerifiedDesignLibraries(raw: unknown): VerifiedDesignLibrariesRead {
  const parsed = verifiedDesignLibrariesManifestSchema.safeParse(raw)
  if (!parsed.success) {
    const first = parsed.error.issues[0]
    const where = first && first.path.length > 0 ? first.path.join('.') : '(root)'
    return {
      status: 'unreadable',
      reason: `${where}: ${first?.message ?? 'manifest does not match the expected schema'}`,
    }
  }
  return { status: 'ready', manifest: parsed.data }
}

export function readVerifiedDesignLibraries(): VerifiedDesignLibrariesRead {
  return parseVerifiedDesignLibraries(manifestJson)
}
